/* eslint-disable no-underscore-dangle */
/**
 * THE review rows for trip claims — one for a receipt, one for an overtime
 * entry — rendered by every screen that reviews claims: the booking's Driver
 * Events tab, the Order Details banner and the Trip claims page. Only the
 * surrounding layout differs; a cross-booking list passes `trip` so the row
 * also names its booking and trip date.
 *
 * A receipt row also carries what the photo said — one badge naming the field
 * the scan disagrees with, and an expandable comparison. The wording and the
 * precedence are `Utils/tripClaims`'s `receiptScanBadge`, never worked out
 * here; see `ReceiptScanBadge` below. An overtime entry has no photo and no
 * scan, so none of it touches that path.
 *
 * Rows are presentational: the caller owns the data and the one review path
 * (TripClaimsService) and passes `onReview(decision)` / `onCreateLineItem()`.
 */
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button } from 'react-bootstrap';
import { formatDateTime } from '../../Utils/timezone';
import { formatAmount } from '../../Utils/billingoDocuments';
import { entryMinutes, isRunningTimer, overtimeReviewStatus } from '../../Utils/driverOvertime';
import {
  claimStatusMeta, claimSubmitterLabel, driverEventsLink, receiptScanBadge,
} from '../../Utils/tripClaims';
import ReceiptCostDestination from './ReceiptCostDestination';

export const RECEIPT_TYPE_LABELS = { parking: 'Parking', other: 'Other' };
export const OVERTIME_STAGE_LABELS = { pickup: 'At pickup', dropoff: 'At drop-off' };
export const OVERTIME_METHOD_LABELS = { timer: 'Timed', manual: 'Reported by driver' };

export function ClaimStatusBadge({ status }) {
  const meta = claimStatusMeta(status);
  return <Badge bg={meta.bg} text={meta.text}>{meta.label}</Badge>;
}

/**
 * What the photo said, as one badge beside the amount. The wording and the
 * precedence are `Utils/tripClaims`'s `receiptScanBadge`; this only draws it.
 *
 * The icon is not decoration. These badges are read in a column of sixty and
 * three of them are tinted the same amber, so the glyph is what separates a
 * disagreement from a date at a glance — and it is what a dispatcher with a
 * colour deficiency has instead of the tint.
 */
export function ReceiptScanBadge({ expense }) {
  const scan = receiptScanBadge(expense);
  if (!scan) return null;
  return (
    <Badge bg={scan.bg} text={scan.text} className="border" data-testid="receipt-scan-badge" data-scan={scan.key}>
      <i className={`fa ${scan.icon} me-1`} />
      {scan.label}
    </Badge>
  );
}

/**
 * "Compare with the photo" — the reading beside what the driver typed, the
 * line the total was read off, and who read it when.
 *
 * INLINE, NOT A SECOND SCREEN. The mockup drew this as its own view off the
 * row, but every control it needs to end in is already ON the row: the
 * approve/reject pair and the cost destination, which together are the ONE
 * action that writes the decision and the ledger line in a single call
 * (see ReceiptReviewRow). A separate view would have had to grow its own copy
 * of that, and two approval paths is how the ledger and the decision come
 * apart. The Order Ledger is a tab of Order Details rather than a second
 * modal for the same reason.
 *
 * WHAT IS DELIBERATELY NOT HERE: the mockup's "File it as 4 500 Ft — as
 * printed on the receipt" radio, which would approve the receipt for an
 * amount other than the one submitted. `POST /operators/portal-expenses`
 * takes a decision and an order line and no amount, so that control would
 * have been inert — and an inert control that looks live is worse than an
 * absent one. If it is wanted, the API has to take the override first, and
 * then it belongs next to the Approve button, not in here.
 *
 * It renders for a decided receipt too, not only a pending one: "why was this
 * approved" is asked more often than "should I approve this".
 */
function ScanComparison({ expense }) {
  const [open, setOpen] = useState(false);
  const { scan } = expense;
  if (!scan || scan.state !== 'read') return null;

  const read = scan.read || {};
  const photoTotal = read.total != null
    ? formatAmount(read.total, read.currency || expense.currency)
    : null;
  const rows = [
    {
      field: 'Total',
      photo: photoTotal,
      typed: formatAmount(expense.amount, expense.currency),
      disagrees: Boolean(scan.amount_mismatch),
    },
    { field: 'Dated', photo: formatDateTime(read.occurred_at), typed: null },
    { field: 'Place', photo: read.merchant || null, typed: null },
  ];

  return (
    <div className="mt-2">
      <Button
        size="sm"
        variant="link"
        className="p-0 text-decoration-none"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        data-testid="scan-comparison-toggle"
      >
        <i className={`fa ${open ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} />
        {open ? 'Hide the photo’s reading' : 'Compare with the photo'}
      </Button>

      {open && (
        <div className="border rounded mt-2 small" data-testid="scan-comparison">
          <div className="d-flex gap-3 px-2 py-1 bg-light border-bottom fw-semibold text-muted">
            <span style={{ flex: '0 0 5rem' }}>Field</span>
            <span className="flex-fill">On the photo</span>
            <span className="flex-fill">Driver typed</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.field}
              className={`d-flex gap-3 px-2 py-1 border-bottom ${row.disagrees ? 'bg-warning-subtle fw-semibold' : ''}`}
            >
              <span className="text-muted" style={{ flex: '0 0 5rem' }}>{row.field}</span>
              <span className="flex-fill">{row.photo || <span className="text-muted fst-italic">not read</span>}</span>
              {/* The driver is never asked for the date or the merchant, so
                  an empty cell here is not a gap in the claim. */}
              <span className="flex-fill">
                {row.typed || <span className="text-muted">never asked for</span>}
              </span>
            </div>
          ))}
          {read.total_source_line && (
            <div className="px-2 py-2 border-bottom">
              <div className="text-muted">The line it read the total from</div>
              {/* `pre-wrap`, because the run of spaces in "Összesen:  4 500 Ft"
                  is the receipt's own column alignment and is half of why this
                  line is worth showing at all — HTML collapses it otherwise,
                  and a <code> block does not opt out by itself. */}
              <code className="d-block mt-1 text-body" style={{ whiteSpace: 'pre-wrap' }}>
                {read.total_source_line}
              </code>
            </div>
          )}
          {/* The ONE place the fact that a model ran is said out loud — a
              dispatcher who has opened this has decided to look closely, and
              knowing which model read it is what makes a wrong reading
              reportable. It never appears on the row itself. */}
          {scan.model && (
            <div className="px-2 py-1 text-muted" data-testid="scan-provenance">
              <i className="fa fa-wand-magic-sparkles me-1" />
              {`Read by ${scan.model}`}
              {scan.scanned_at ? ` on ${formatDateTime(scan.scanned_at)}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The booking a claim belongs to — on cross-booking lists only. */
export function ClaimTrip({ trip }) {
  if (!trip) return null;
  return (
    <div className="small mt-1" data-testid="claim-trip">
      <i className="fa fa-car me-1 text-muted" />
      {trip.bookingId ? (
        <Link to={driverEventsLink(trip.bookingId)} className="fw-semibold">
          {trip.reference || 'Booking'}
        </Link>
      ) : (
        <span className="text-muted">Not tied to a trip</span>
      )}
      {trip.tripDate && (
        <span className="text-muted">
          {' · trip '}
          {formatDateTime(trip.tripDate)}
        </span>
      )}
      {trip.status && <span className="text-muted">{` · ${trip.status}`}</span>}
    </div>
  );
}

/** Who reviewed it and when, once it has been decided. */
function Reviewed({ status, by, at }) {
  if (!at) return null;
  return (
    <>
      {' • '}
      {claimStatusMeta(status).label}
      {by ? ` by ${by}` : ''}
      {' '}
      {formatDateTime(at)}
    </>
  );
}

function ReviewActions({
  pending, approved, busy, onReview, onCreateLineItem, warning = '',
  approveLabel = 'Approve', approveDisabled = false, children = null,
}) {
  if (!pending && !(approved && onCreateLineItem)) return null;
  return (
    <>
      {pending && warning && (
        <div className="small text-muted mt-2" data-testid="claim-review-warning">
          <i className="fa fa-circle-info me-1" />
          {warning}
        </div>
      )}
      {pending && children}
      <div className="d-flex flex-wrap gap-2 mt-2">
        {pending && (
          <>
            <Button
              size="sm"
              variant="outline-success"
              disabled={busy || approveDisabled}
              onClick={() => onReview('approved')}
            >
              <i className="fa fa-check me-1" />
              {approveLabel}
            </Button>
            <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => onReview('rejected')}>
              <i className="fa fa-times me-1" />
              Reject
            </Button>
          </>
        )}
        {approved && onCreateLineItem && (
          <Button size="sm" variant="outline-primary" onClick={onCreateLineItem}>
            <i className="fa fa-plus me-1" />
            Create line item
          </Button>
        )}
      </div>
    </>
  );
}

/**
 * A receipt: its photo (the presigned link), type, amount, status, who filed
 * it, the note, and approve/reject — or "Create line item" once approved.
 *
 * APPROVING IS ONE ACTION. A pending receipt that is tied to a booking also
 * shows where its cost would go — pre-selected, never asked blind — and
 * "Approve & add line" sends the decision and the destination together, so
 * the API writes the ledger line and records the approval in one call and the
 * two cannot diverge. Picking "Absorbed" is the same button with no line.
 * It is possible on any receipt but has to be chosen: the pre-selection files
 * a line (see `Utils/expenseCostDestination`).
 *
 * A receipt filed against no booking has no ledger to write to, so it keeps
 * the plain Approve it always had. `canFileLine` is the ONE place that rule
 * lives. `preselectedDestination` does not repeat it, so any screen that
 * shows the picker has to keep this gate.
 */
export function ReceiptReviewRow({
  expense, trip, busy = false, onReview, onCreateLineItem, onOpenPhoto,
}) {
  const pending = expense.status === 'submitted';
  const canFileLine = pending && Boolean(expense.booking_id) && Boolean(onReview);
  const [choice, setChoice] = useState(null);
  const scanDetail = receiptScanBadge(expense)?.detail || '';

  // Stable, so the destination block's effect does not re-fire every render.
  const handleDestination = useCallback((next) => setChoice(next), []);

  const review = (decision) => {
    // Only an approval carries a destination; a rejection never files a line.
    if (decision !== 'approved' || !canFileLine) return onReview(decision);
    return onReview(decision, choice?.orderLine);
  };
  return (
    <div className="d-flex gap-3 border-bottom py-2" data-testid={`driver-expense-${expense._id}`} data-claim-kind="receipt">
      {expense.photo_url ? (
        <a
          href={expense.photo_url}
          target="_blank"
          rel="noreferrer"
          title="Open receipt in a new tab"
          onClick={(e) => onOpenPhoto?.(e, expense)}
          data-testid="expense-photo-link"
        >
          <img
            src={expense.photo_url}
            alt="Receipt"
            className="rounded border"
            style={{ width: 72, height: 72, objectFit: 'cover' }}
          />
        </a>
      ) : (
        <div
          className="rounded border text-muted small d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 72, height: 72 }}
        >
          No photo
        </div>
      )}

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg="light" text="dark" className="border">
            <i className="fa fa-receipt me-1" />
            {RECEIPT_TYPE_LABELS[expense.type] || expense.type || 'Other'}
          </Badge>
          <span className="fw-bold">{formatAmount(expense.amount, expense.currency)}</span>
          <ClaimStatusBadge status={expense.status} />
          <ReceiptScanBadge expense={expense} />
        </div>
        <ClaimTrip trip={trip} />
        <div className="small mt-1" data-testid="expense-submitter">{claimSubmitterLabel(expense)}</div>
        {scanDetail && (
          <div className="small text-muted mt-1" data-testid="receipt-scan-detail">{scanDetail}</div>
        )}
        {expense.note && <div className="small mt-1 text-break">{expense.note}</div>}
        <div className="small text-muted mt-1">
          Submitted
          {' '}
          {formatDateTime(expense.created_at)}
          <Reviewed status={expense.status} by={expense.reviewed_by} at={expense.reviewed_at} />
        </div>
        <ScanComparison expense={expense} />
        <ReviewActions
          pending={pending}
          approved={expense.status === 'approved'}
          busy={busy}
          onReview={review}
          onCreateLineItem={onCreateLineItem}
          approveLabel={canFileLine && choice?.orderLine ? 'Approve & add line' : 'Approve'}
          approveDisabled={canFileLine && choice ? !choice.ready : false}
        >
          {canFileLine && (
            <ReceiptCostDestination
              expense={expense}
              onChange={handleDestination}
              disabled={busy}
            />
          )}
        </ReviewActions>
      </div>
    </div>
  );
}

function TimeRange({ entry }) {
  if (entry.method !== 'timer') return null;
  return (
    <div className="small text-muted mt-1">
      {formatDateTime(entry.started_at) || '—'}
      {' – '}
      {entry.ended_at ? formatDateTime(entry.ended_at) : 'still running'}
    </div>
  );
}

/**
 * Deciding a timer that is still going is what stops it — the only way out
 * for a trip whose driver never tapped "stop" (a dead phone), which used to
 * leave the booking unclosable and uninvoiceable for good.
 */
export const RUNNING_TIMER_REVIEW_WARNING = 'This timer is still running. Approving stops it and bills '
  + 'the minutes up to now; rejecting stops it and bills nothing.';

/**
 * An overtime entry: where and how, the minutes (or "Still running"), who
 * filed it when known, the note, and approve/reject — or "Create line item"
 * once approved. A running timer is decided like any other, and the decision
 * stops its clock; the row says so before staff press anything.
 */
export function OvertimeReviewRow({
  entry, trip, busy = false, onReview, onCreateLineItem,
}) {
  const running = isRunningTimer(entry);
  const decision = overtimeReviewStatus(entry);
  const status = running ? 'running' : decision;
  const minutes = entryMinutes(entry);
  return (
    <div className="d-flex gap-3 border-bottom py-2" data-testid="overtime-entry" data-claim-kind="overtime">
      <div
        className="rounded border text-muted d-flex flex-column align-items-center justify-content-center flex-shrink-0"
        style={{ width: 72, height: 72 }}
      >
        <i className="fa fa-hourglass-half" />
        <span className="small fw-bold text-dark" data-testid="overtime-entry-minutes">
          {minutes != null ? `${minutes} min` : '—'}
        </span>
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg="light" text="dark" className="border">
            {OVERTIME_STAGE_LABELS[entry.stage] || entry.stage || '—'}
          </Badge>
          <span className="small">{OVERTIME_METHOD_LABELS[entry.method] || entry.method || ''}</span>
          <ClaimStatusBadge status={status} />
        </div>
        <ClaimTrip trip={trip} />
        {entry.submitted_by && <div className="small mt-1">{claimSubmitterLabel(entry)}</div>}
        {entry.note && <div className="small mt-1 text-break">{entry.note}</div>}
        <TimeRange entry={entry} />
        {(entry.created_at || entry.reviewed_at) && (
          <div className="small text-muted mt-1">
            {entry.created_at && (
              <>
                Recorded
                {' '}
                {formatDateTime(entry.created_at)}
              </>
            )}
            <Reviewed status={status} by={entry.reviewed_by} at={entry.reviewed_at} />
          </div>
        )}
        <ReviewActions
          pending={decision === 'pending'}
          approved={decision === 'approved'}
          busy={busy}
          onReview={onReview}
          onCreateLineItem={onCreateLineItem}
          warning={running ? RUNNING_TIMER_REVIEW_WARNING : ''}
        />
      </div>
    </div>
  );
}
